/* LouMakeupAnalysis — upload face photo → AI analyzes features + recommends makeup */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Upload, Sparkles, Wand2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { key: 'face_shape',      label: '🌸 Ansiktsform' },
  { key: 'eye_color',       label: '👁️ Ögonfärg' },
  { key: 'eye_shape',       label: '👀 Ögonform' },
  { key: 'lip_shape',       label: '💋 Läppform' },
  { key: 'cheek_structure', label: '🌷 Kindstruktur' },
  { key: 'brow_shape',      label: '✨ Bryn' },
  { key: 'skin_undertone',  label: '🎨 Hudunderton' },
];

const SLOTS = [
  { key: 'base',    label: '🌿 Bas (foundation/concealer)' },
  { key: 'cheeks',  label: '🌷 Kinder (contour/blush/highlight)' },
  { key: 'eyes',    label: '👁️ Ögon' },
  { key: 'brows',   label: '✨ Bryn' },
  { key: 'lips',    label: '💋 Läppar' },
];

export default function LouMakeupAnalysis({ onClose }) {
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [tutorial, setTutorial] = useState(null);
  const [tutorialBusy, setTutorialBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/experts/lou/makeup-analysis`, { withCredentials: true });
        if (r.data?.analysis) setAnalysis(r.data.analysis);
      } catch {/**/}
      try {
        const t = await axios.get(`${API}/experts/lou/makeup-tutorial`, { withCredentials: true });
        if (t.data?.tutorial) setTutorial(t.data.tutorial);
      } catch {/**/}
    })();
  }, []);

  const generateTutorial = async () => {
    if (tutorialBusy) return;
    setTutorialBusy(true);
    try {
      const r = await axios.post(`${API}/experts/lou/makeup-tutorial`, {}, { withCredentials: true, timeout: 180000 });
      if (r.data?.tutorial) setTutorial(r.data.tutorial);
      toast.success('✨ Din 5-stegs-tutorial är klar 💄');
    } catch (e) {
      const det = e?.response?.data?.detail || '';
      if (e?.response?.status === 503) toast.error('LLM-nyckel saknas — toppa upp');
      else toast.error('Kunde inte generera tutorial: ' + det);
    } finally {
      setTutorialBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/experts/lou/makeup-analysis`, fd, { withCredentials: true, timeout: 180000 });
      setAnalysis(r.data);
      toast.success('💄 Lou har analyserat dina drag 💜');
    } catch (e) {
      toast.error('Kunde inte analysera: ' + (e?.response?.data?.detail || ''));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const rec = analysis?.makeup_recommendations || {};

  return (
    <div
      className="fixed inset-0 z-[345] bg-black/75 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="makeup-modal"
    >
      <div
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl bg-[#FAF6F0] max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 bg-gradient-to-r from-rose-50 to-stone-100 border-b border-stone-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Lou · stylist</p>
            <h2 className="font-serif text-2xl font-bold text-stone-800 leading-tight">Smink-analys 💄</h2>
            <p className="text-[11px] text-stone-500 italic">Ladda upp en ansikts-bild så lyfter Lou fram dina finaste drag</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-200" data-testid="makeup-close">
            <X className="w-5 h-5 text-stone-700" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <section className="rounded-2xl bg-white border border-stone-200 p-3">
            <label className="block w-full py-3 rounded-xl border-2 border-dashed border-rose-300 hover:bg-rose-50 cursor-pointer text-center text-rose-700 font-bold text-sm" data-testid="makeup-upload-label">
              <Upload className="w-4 h-4 inline mr-1" /> {analysis ? 'Ladda upp ny bild' : 'Ladda upp ansikts-foto'}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="makeup-upload-input" />
            </label>
            <p className="text-[10px] text-stone-400 italic text-center mt-1.5">Använd helst en bild utan smink, naturligt ljus, rakt framifrån</p>
          </section>

          {busy && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-rose-500" />
              <p className="text-xs italic text-rose-600 mt-2">Lou studerar dina drag… (~30-60 sek)</p>
            </div>
          )}

          {previewUrl && analysis && !busy && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl overflow-hidden bg-white border border-stone-200">
                <img src={previewUrl} alt="Du" className="w-full aspect-square object-cover" />
              </div>
              {analysis.products_image_base64 && (
                <div className="rounded-2xl overflow-hidden bg-white border border-stone-200">
                  <img
                    src={`data:${analysis.products_image_mime || 'image/png'};base64,${analysis.products_image_base64}`}
                    alt="Smink-produkter"
                    className="w-full aspect-square object-cover"
                    data-testid="makeup-products-image"
                  />
                </div>
              )}
            </div>
          )}

          {analysis && !busy && (
            <>
              {/* Best features highlight */}
              {analysis.best_features?.length > 0 && (
                <section className="rounded-2xl bg-gradient-to-r from-rose-100 to-amber-50 border border-rose-200 p-3" data-testid="makeup-best-features">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-rose-700 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Dina finaste drag
                  </p>
                  <ul className="space-y-1 mt-1.5">
                    {analysis.best_features.map((f, i) => (
                      <li key={i} className="text-sm text-stone-800 leading-snug" data-testid={`makeup-best-${i}`}>💖 {f}</li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Feature analysis grid */}
              <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Drag-analys</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {FEATURES.map((f) => analysis[f.key] && (
                    <div key={f.key} className="rounded-xl bg-stone-50 border border-stone-200 px-2.5 py-1.5" data-testid={`makeup-feature-${f.key}`}>
                      <p className="text-[9px] uppercase tracking-widest font-extrabold text-stone-500">{f.label}</p>
                      <p className="text-[12px] text-stone-800">{analysis[f.key]}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Color palette */}
              {analysis.color_palette?.length > 0 && (
                <section className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Smink-palett som klär dig</p>
                  <div className="flex gap-1.5 flex-wrap" data-testid="makeup-palette">
                    {analysis.color_palette.map((c, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full shadow border-2 border-white" style={{ backgroundColor: c }} />
                        <span className="text-[9px] text-stone-500 font-mono mt-0.5">{c}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Recommendations per slot */}
              <section className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Smink-rekommendationer</p>
                {SLOTS.map((s) => {
                  const r = rec[s.key];
                  if (!r) return null;
                  return (
                    <div key={s.key} className="rounded-2xl bg-white border border-stone-200 p-3" data-testid={`makeup-rec-${s.key}`}>
                      <p className="font-extrabold text-stone-800 text-sm">{s.label}</p>
                      {r.tips && <p className="text-[12px] text-stone-700 italic mt-1 leading-snug">{r.tips}</p>}
                      {Array.isArray(r.products) && r.products.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {r.products.map((p, pi) => (
                            <span key={pi} className="text-[10px] bg-rose-50 border border-rose-200 text-rose-700 px-2 py-0.5 rounded-full">{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>

              {/* Signature look */}
              {analysis.signature_look && (
                <section className="rounded-2xl bg-gradient-to-r from-violet-100 to-rose-100 border border-violet-200 p-3" data-testid="makeup-signature">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-violet-700 flex items-center gap-1">
                    <Wand2 className="w-3 h-3" /> Ditt signature-look
                  </p>
                  <p className="text-sm font-serif italic text-violet-900 leading-snug mt-1">{analysis.signature_look}</p>
                </section>
              )}

              {/* 📚 Tutorial section */}
              <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2" data-testid="makeup-tutorial-section">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600 flex items-center gap-1">
                    <BookOpen className="w-3 h-3" /> 5-stegs tutorial
                  </p>
                  <button
                    onClick={generateTutorial}
                    disabled={tutorialBusy}
                    data-testid="makeup-tutorial-generate"
                    className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                  >
                    {tutorialBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    {tutorial ? 'Generera ny' : 'Bygg min tutorial'}
                  </button>
                </div>
                {tutorialBusy && (
                  <p className="text-[11px] italic text-rose-600 text-center py-3">
                    Lou skissar din personliga 5-stegs-tutorial… (~60-90 sek)
                  </p>
                )}
                {!tutorialBusy && !tutorial && (
                  <p className="text-[11px] italic text-stone-500 text-center py-3">
                    Tryck "Bygg min tutorial" så får du 5 steg med varsin AI-bild att följa när du sminkar dig 💜
                  </p>
                )}
                {!tutorialBusy && tutorial?.steps?.length > 0 && (
                  <ol className="space-y-2.5">
                    {tutorial.steps.map((s, i) => (
                      <li key={i} className="rounded-xl bg-stone-50 border border-stone-200 overflow-hidden" data-testid={`makeup-step-${i}`}>
                        {s.image_base64 && (
                          <img
                            src={`data:${s.image_mime || 'image/png'};base64,${s.image_base64}`}
                            alt={`Steg ${s.n}`}
                            className="w-full h-auto block"
                            loading="lazy"
                          />
                        )}
                        <div className="p-3">
                          <p className="font-extrabold text-stone-800 text-sm flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-rose-600 text-white text-xs flex items-center justify-center shrink-0">{s.n}</span>
                            {s.title}
                          </p>
                          <p className="text-[12px] text-stone-700 leading-snug mt-1">{s.instruction}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
